import { ReactNode, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@src/app/store";

interface ThemeWrapperProps {
  children: ReactNode;
}
const ThemeWrapper: React.FC<ThemeWrapperProps> = ({ children }) => {
  const themeName = useSelector((state: RootState) => state.globalSlice.theme);

  const theme = useMemo(() => {
    if (themeName === "website-light-theme") {
      return {
        bgColor1: "#008C33", // Green (Primary)
        bgColorLightGreen: "rgba(0, 140, 51, 0.1)",
        bgColorGreenHover: "#0e291a",
        greenDelete400: "#008c33",
        bgColor2: "#f1f5f9",
        bgColor3: "#ffffff",
        bgColor4: "#f8fafc",
        bgColor5: "#ffffff",
        bgColor6: "#ffffff",
        bgColor7: "#f5f3ff",
        bgColor8: "#f8fafc",
        color1: "#475569",
        color2: "#16a34a",
        color3: "#dc2626",
        color4: "#d97706",
        color5: "#64748b",
        color6: "#0f172a", // main dark text instead of white text
        color7: "#ea580c",
        color8: "#ca8a04",
        color9: "#15803d",
        color10: "rgba(71, 85, 105, 0.08)",
        colorGreenDark: "#dcfce7",
        colorGreenDarkHover: "#bbf7d0",
        colorOrangeDark: "#fef3c7",
        colorOrangeDarkHover: "#fde68a",
        colorRedDark: "#fee2e2",
        colorRedDarkHover: "#fecaca",
        grayLight: "#94a3b8",
        blackMedium: "#f1f5f9",
        blackMediumHover: "#e2e8f0",
        blackShadeSix: "#f8fafc",
        blackShade9: "#f1f5f9",
        multiselecttxtcolor: "#475569",
        multiselectbordercolor: "#cbd5e1",
        shadow1: "rgba(0, 140, 51, 0.08)",
        hoverGrayBg: "#cbd5e1",
        purpleHover: "#ede9fe",
        purpleText: "#7c3aed",
      };
    }

    return {
      bgColor1: "#008C33",
      bgColorLightGreen: "rgba(0, 140, 51, 0.35)",
      bgColorGreenHover: "#16552D",
      greenDelete400: "#189847",
      bgColor2: "#373737",
      bgColor3: "#111111",
      bgColor4: "#1E1E1E",
      bgColor5: "#0F120A",
      bgColor6: "#000000",
      bgColor7: "#4C336C",
      bgColor8: "#0F0F0F",
      color1: "#A1A1A1",
      color2: "#00FF3C",
      color3: "#B90F10",
      color4: "#FFC000",
      color5: "#818181",
      color6: "#FFFFFF",
      color7: "#FF6B47",
      color8: "#E89D52",
      color9: "#42AB5B",
      color10: "rgba(161, 161, 161, 0.1)",
      colorGreenDark: "#0E291A",
      colorGreenDarkHover: "#215437",
      colorOrangeDark: "#242200",
      colorOrangeDarkHover: "#4D4A00",
      colorRedDark: "#3B2627",
      colorRedDarkHover: "#6B494A",
      grayLight: "#808080",
      blackMedium: "#141414",
      blackMediumHover: "#252525",
      blackShadeSix: "#161616",
      blackShade9: "#090909",
      multiselecttxtcolor: "#a5a5a5",
      multiselectbordercolor: "#2d2d2d",
      shadow1: "#D4F39A40",
      hoverGrayBg: "#5d5d5d",
      purpleHover: "#583A80",
      purpleText: "#8A38F5",
    };
  }, [themeName]);

  useEffect(() => {
    document.body.classList.toggle(
      "website-light-theme",
      themeName === "website-light-theme"
    );
    const root = document.documentElement;
    root.style.setProperty("--bg-color1", theme.bgColor1);
    root.style.setProperty("--bg-color-light-green", theme.bgColorLightGreen);
    root.style.setProperty("--bg-color2", theme.bgColor2);
    root.style.setProperty("--bg-color3", theme.bgColor3);
    root.style.setProperty("--bg-color4", theme.bgColor4);
    root.style.setProperty("--bg-color5", theme.bgColor5);
    root.style.setProperty("--bg-color6", theme.bgColor6);
    root.style.setProperty("--bg-color7", theme.bgColor7);
    root.style.setProperty("--bg-color8", theme.bgColor8);
    root.style.setProperty("--color1", theme.color1);
    root.style.setProperty("--color2", theme.color2);
    root.style.setProperty("--color3", theme.color3);
    root.style.setProperty("--color4", theme.color4);
    root.style.setProperty("--color5", theme.color5);
    root.style.setProperty("--color6", theme.color6);
    root.style.setProperty("--color7", theme.color7);
    root.style.setProperty("--color8", theme.color8);
    root.style.setProperty("--color9", theme.color9);
    root.style.setProperty("--color10", theme.color10);
    root.style.setProperty("--color-green-dark", theme.colorGreenDark);
    root.style.setProperty(
      "--color-green-dark-hover",
      theme.colorGreenDarkHover
    );
    root.style.setProperty("--color-orange-dark", theme.colorOrangeDark);
    root.style.setProperty(
      "--color-orange-dark-hover",
      theme.colorOrangeDarkHover
    );
    root.style.setProperty("--color-red-dark", theme.colorRedDark);
    root.style.setProperty("--color-red-dark-hover", theme.colorRedDarkHover);
    root.style.setProperty("--gray-light1", theme.grayLight);
    root.style.setProperty(
      "--multiselect-txt-color",
      theme.multiselecttxtcolor
    );
    root.style.setProperty(
      "--multiselect-border-color",
      theme.multiselectbordercolor
    );
    root.style.setProperty("--shadow1", theme.shadow1);
    root.style.setProperty("--hover-gray-bg", theme.hoverGrayBg);
    root.style.setProperty("--purple-hover", theme.purpleHover);
    root.style.setProperty("--purple-text", theme.purpleText);
    root.style.setProperty("--black-medium", theme.blackMedium);
    root.style.setProperty("--black-medium-hover", theme.blackMediumHover);
    root.style.setProperty("--black-shade-six", theme.blackShadeSix);
    root.style.setProperty("--bg-color-green-hover", theme.bgColorGreenHover);
    root.style.setProperty("--green-delete-400", theme.greenDelete400);
    root.style.setProperty("--black-shade-nine", theme.blackShade9);
  }, [theme, themeName]);

  return children;
};

export default ThemeWrapper;
